import React, { useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { SurveyContext } from '@context/SurveyContext'
import {
  categorySurveyDisplayName,
  getCategory,
  sortedCategorySurveys,
  stripEditionSurveyLabel,
} from '@utils/categoryFlow'
import gotchiIcon from '@assets/images/characters/gotchi.png'
import './SandboxQuestionnairePicker.scss'

export function SandboxQuestionnairePicker() {
  const { t, i18n } = useTranslation('Simulation')
  const {
    survey,
    portraitBySurveyKey = {},
    switchSandboxSurvey,
  } = useContext(SurveyContext)

  const category = getCategory(survey)
  const surveys = useMemo(() => sortedCategorySurveys(category), [category])
  const lang = (i18n.language ?? 'fr').substring(0, 2)

  if (surveys.length < 2) {
    return null
  }

  const activeKey = survey?.key
  const lastIndex = surveys.length - 1

  return (
    <div className="SandboxQuestionnairePicker">
      <div
        className="SandboxQuestionnairePicker-thumbs"
        role="group"
        aria-label={t('sandbox.pickQuestionnaire')}
      >
        {surveys.map((ref, index) => {
          const rawLabel =
            categorySurveyDisplayName(ref, lang) ||
            t('sandbox.questionnaireFallback', { index: index + 1 })
          const label = stripEditionSurveyLabel(rawLabel, category)
          const thumb = portraitBySurveyKey[ref.key]
          const selected = ref.key === activeKey
          const isFinal = index >= lastIndex
          const badge = isFinal ? t('sandbox.badgePrediction') : t('sandbox.badgeIntermediate')
          return (
            <button
              key={ref.key}
              type="button"
              className={[
                'SandboxQuestionnairePicker-item',
                selected ? 'SandboxQuestionnairePicker-item--selected' : '',
                selected
                  ? isFinal
                    ? 'SandboxQuestionnairePicker-item--final'
                    : 'SandboxQuestionnairePicker-item--intermediate'
                  : '',
              ]
                .filter(Boolean)
                .join(' ')}
              aria-pressed={selected}
              aria-label={`${label}. ${badge}`}
              onClick={() => {
                void switchSandboxSurvey(ref.key)
              }}
            >
              <span className="SandboxQuestionnairePicker-thumb">
                <img src={thumb || gotchiIcon} alt="" />
                <span
                  className={[
                    'SandboxQuestionnairePicker-badge',
                    isFinal
                      ? 'SandboxQuestionnairePicker-badge--final'
                      : 'SandboxQuestionnairePicker-badge--intermediate',
                  ].join(' ')}
                >
                  {badge}
                </span>
              </span>
              <span className="SandboxQuestionnairePicker-label">{label}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

export default SandboxQuestionnairePicker
